/**
 * Main Frontend App - Tenant Admin Dashboard
 */
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, alpha, createTheme } from '@mui/material/styles';
import React from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import ModuleAccessGuard from '@components/auth/ModuleAccessGuard';
import ProtectedRoute from '@components/auth/ProtectedRoute';
import AdminLayout from '@components/layout/AdminLayout';
import Home from '@pages/home';
import Contact from '@pages/contact';
import Signup from '@pages/Signup';
import Onboarding from '@pages/auth/Onboarding';
import OnboardingPending from '@pages/auth/OnboardingPending';
import GoogleConfirm from '@pages/auth/GoogleConfirm';
import InviteAccept from '@pages/invite/InviteAccept';
import PrivacyPolicy from '@pages/PrivacyPolicy';
import Agents from '@pages/admin/Agents';
import BillingAI from '@pages/admin/BillingAI';
import BillingActivePlan from '@pages/admin/BillingActivePlan';
import BillingAmount from '@pages/admin/BillingAmount';
import BillingUsageHistory from '@pages/admin/BillingUsageHistory';
import BillingWallet from '@pages/admin/BillingWallet';
import Contacts from '@pages/admin/Contacts';
import ContactDetail from '@pages/admin/ContactDetail';
import AdminDashboard from '@pages/admin/Dashboard';
import Integrations from '@pages/admin/Integrations';
import LLMConfig from '@pages/admin/LLMConfig';
import AdminLogin from '@pages/admin/Login';
import { ForgotPassword, ResetPassword } from '@pages/auth/PasswordRecovery';
import Playground from '@pages/admin/Playground';
import Settings from '@pages/admin/Settings';
import MyAccount from '@pages/admin/MyAccount';
import SystemAlerts from '@pages/admin/SystemAlerts';
import WhatsAppTemplateNew from '@pages/admin/WhatsAppTemplateNew';
import WhatsAppTemplates from '@pages/admin/WhatsAppTemplates';
import WhatsAppInteractiveMessages from '@pages/admin/WhatsAppInteractiveMessages';
import InteractiveMessageEditor from '@pages/admin/whatsapp/InteractiveMessageEditor';
import Welcome from '@pages/admin/Welcome';
import ChatLogs from '@pages/admin/activity/ChatLogs';
import Leads from '@pages/admin/activity/Leads';
import InboundMedia from '@/pages/admin/activity/InboundMedia';
import InstagramComments from '@/pages/admin/activity/InstagramComments';
import WebChats from '@pages/admin/WebChats';
import Appointments from '@pages/admin/Appointments';
import ChatsAnalytics from '@pages/admin/analytics/Chats';
import Reports from '@pages/admin/Reports';
import CampaignList from '@pages/admin/campaigns/CampaignList';
import CampaignForm from '@pages/admin/campaigns/CampaignForm';
import CampaignDetail from '@pages/admin/campaigns/CampaignDetail';
import Workflows from '@pages/admin/automation/Workflows';
import WorkflowExecutions from '@pages/admin/automation/WorkflowExecutions';
import WorkflowCanvas from '@pages/admin/automation/WorkflowCanvas';
import ApiConnections from '@pages/admin/automation/ApiConnections';
import ApiConnectionEditor from '@pages/admin/automation/ApiConnectionEditor';
import FilesSource from '@pages/admin/sources/Files';
import QASource from '@pages/admin/sources/QA';
import TextSource from '@pages/admin/sources/Text';
import MediaSource from '@pages/admin/sources/Media';
import UserManagement from '@pages/admin/user-management/UserManagement';
import LeadCapture from '@pages/public/LeadCapture';
import LeadForm from '@pages/admin/LeadForm';

/** The template editor used to live under the Settings › WhatsApp tab. It is
 *  now a first-class page (sidebar › WhatsApp Templates), so the old URL
 *  forwards there — keeping bookmarks and the edit-draft `state` working. */
function LegacyTemplateNewRedirect() {
  const location = useLocation();
  const prefix = location.pathname.startsWith('/admin') ? '/admin' : '';
  return <Navigate to={`${prefix}/whatsapp-templates/new`} replace state={location.state} />;
}

const P = '#4f46e5';
const theme = createTheme({
  palette: {
    primary: { main: P, dark: '#4338ca', contrastText: '#fff' },
    secondary: { main: '#f97316' },
    success: { main: '#10b981' },
    warning: { main: '#f59e0b' },
    error: { main: '#ef4444' },
    info: { main: '#3b82f6' },
    background: { default: '#f8fafc', paper: '#ffffff' },
    text: { primary: '#0f172a', secondary: '#64748b', disabled: '#94a3b8' },
    divider: '#e2e8f0',
  },
  typography: {
    fontFamily: '"Inter", "DM Sans", system-ui, -apple-system, sans-serif',
    h1: { fontSize: '2.25rem', fontWeight: 700, letterSpacing: '-0.025em' },
    h2: { fontSize: '1.875rem', fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.015em' },
    h4: { fontSize: '1.25rem', fontWeight: 600, letterSpacing: '-0.01em' },
    h5: { fontSize: '1.125rem', fontWeight: 600 },
    h6: { fontSize: '1rem', fontWeight: 600 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.6 },
    caption: { fontSize: '0.75rem', lineHeight: 1.5, letterSpacing: '0.01em' },
    button: { fontWeight: 500, textTransform: 'none' as const, letterSpacing: '0.01em' },
    overline: { fontWeight: 600, letterSpacing: '0.1em', fontSize: '0.6875rem' },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontSize: '0.875rem',
          padding: '8px 18px',
          transition: 'all 0.15s ease',
        },
        contained: {
          '&:hover': { transform: 'translateY(-1px)', boxShadow: `0 4px 12px ${alpha(P, 0.3)}` },
          '&:active': { transform: 'translateY(0)' },
        },
        outlined: {
          borderColor: '#e2e8f0',
          '&:hover': { borderColor: P, backgroundColor: alpha(P, 0.04) },
        },
        sizeSmall: { padding: '5px 12px', fontSize: '0.8125rem' },
        sizeLarge: { padding: '11px 24px', fontSize: '0.9375rem' },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: '1px solid #e2e8f0',
          borderRadius: 12,
          transition: 'box-shadow 0.2s, border-color 0.2s',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: { root: { padding: '20px 24px', '&:last-child': { paddingBottom: 20 } } },
    },
    MuiTextField: {
      defaultProps: { size: 'small' as const },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            fontSize: '0.875rem',
            '& fieldset': { borderColor: '#e2e8f0' },
            '&:hover fieldset': { borderColor: P },
            '&.Mui-focused fieldset': { borderColor: P, borderWidth: 2 },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500, fontSize: '0.75rem', borderRadius: 6, height: 26 },
        sizeSmall: { height: 22, fontSize: '0.6875rem' },
        // Soft-tint filled chips (see `variants` below; pale background + dark text of the same hue) instead of
        // MUI's flat saturated fill — matches components/ui/StatusChip's palette so a raw
        // <Chip color="success"> and a <StatusChip status="active"> read as one system.
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#f8fafc',
            color: '#64748b',
            fontWeight: 600,
            fontSize: '0.75rem',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            borderBottom: '2px solid #e2e8f0',
            padding: '12px 16px',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: alpha(P, 0.025) },
          '&:last-child td': { borderBottom: 0 },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: '#e2e8f0', padding: '12px 16px', fontSize: '0.875rem' },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          border: '1px solid #e2e8f0',
          boxShadow: '0 25px 50px rgba(0,0,0,.15)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontSize: '1.0625rem', fontWeight: 600, padding: '20px 24px 12px' },
      },
    },
    MuiDialogContent: { styleOverrides: { root: { padding: '8px 24px 16px' } } },
    MuiDialogActions: { styleOverrides: { root: { padding: '12px 24px 20px', gap: 8 } } },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 10, fontSize: '0.875rem' } },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 42 },
        indicator: { borderRadius: '2px 2px 0 0', height: 2.5, backgroundColor: P },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 42,
          fontWeight: 500,
          fontSize: '0.875rem',
          textTransform: 'none',
          letterSpacing: 0,
          padding: '8px 16px',
          '&.Mui-selected': { fontWeight: 600, color: P },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: { root: { borderRadius: 8, transition: 'all 0.15s ease' } },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { backgroundImage: 'none' }, rounded: { borderRadius: 12 } },
    },
    MuiLinearProgress: {
      styleOverrides: { root: { borderRadius: 99, height: 6 }, bar: { borderRadius: 99 } },
    },
    MuiSkeleton: { styleOverrides: { root: { borderRadius: 8 } } },
    MuiDivider: { styleOverrides: { root: { borderColor: '#e2e8f0' } } },
  },
});

const platformRoutes = (
  <React.Fragment>
    <Route path="welcome" element={<Welcome />} />
    <Route
      path="dashboard"
      element={
        <ModuleAccessGuard module="dashboard">
          <AdminDashboard />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="playground"
      element={
        <ModuleAccessGuard module="playground">
          <Playground />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="activity/chat-logs"
      element={
        <ModuleAccessGuard module="activity.chatLogs">
          <ChatLogs />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="activity/leads"
      element={
        <ModuleAccessGuard module="activity.leads">
          <Leads />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="activity/media"
      element={
        <ModuleAccessGuard module="activity.media">
          <InboundMedia />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="activity/instagram-comments"
      element={
        <ModuleAccessGuard module="instagramComments">
          <InstagramComments />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="activity/web-captures"
      element={
        <ModuleAccessGuard module="activity.chatLogs">
          <WebChats />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="appointments"
      element={
        <ModuleAccessGuard module="appointments">
          <Appointments />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="activity/integrations"
      element={
        <ModuleAccessGuard module="integrations">
          <Integrations />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="analytics/chats"
      element={
        <ModuleAccessGuard module="analytics.chats">
          <ChatsAnalytics />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="reports"
      element={
        <ModuleAccessGuard module="reports">
          <Reports />
        </ModuleAccessGuard>
      }
    />
    {/* Topics + Sentiment analytics are unbuilt (stub pages, no backend) —
        hidden from the sidebar; bookmarked URLs redirect to Chats instead of
        rendering a "Coming soon" stub. Page files kept for the future build. */}
    <Route path="analytics/topics" element={<Navigate to="../analytics/chats" replace />} />
    <Route path="analytics/sentiment" element={<Navigate to="../analytics/chats" replace />} />
    <Route
      path="sources/files"
      element={
        <ModuleAccessGuard module="sources.files">
          <FilesSource />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="sources/text"
      element={
        <ModuleAccessGuard module="sources.text">
          <TextSource />
        </ModuleAccessGuard>
      }
    />
    {/* sources.website has no backend requirePermission enforcement — redirect
        to /sources/files until the feature is fully implemented. */}
    <Route path="sources/website" element={<Navigate to="../sources/files" replace />} />
    <Route
      path="sources/qa"
      element={
        <ModuleAccessGuard module="sources.qa">
          <QASource />
        </ModuleAccessGuard>
      }
    />
    {/* Media Library is its own top-level destination (/admin/media), no longer
        nested under Sources. Module key stays `sources.media`. The old
        /sources/media URL redirects so existing deep links keep working. */}
    <Route
      path="media"
      element={
        <ModuleAccessGuard module="sources.media">
          <MediaSource />
        </ModuleAccessGuard>
      }
    />
    <Route path="sources/media" element={<Navigate to="../media" replace />} />
    <Route
      path="agents"
      element={
        <ModuleAccessGuard module="agents">
          <Agents />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="contacts"
      element={
        <ModuleAccessGuard module="contacts">
          <Contacts />
        </ModuleAccessGuard>
      }
    />
    {/* Per-contact CRM record. Same 'contacts' gate as the list — there is no
        separate module key for it, and routeToModuleMap needs no entry: it
        matches full paths exactly, so '/contacts/:id' could never be looked
        up there, and hasRouteAccess's "not in the map => allow" fallback plus
        this guard already give the right answer. */}
    <Route
      path="contacts/:id"
      element={
        <ModuleAccessGuard module="contacts">
          <ContactDetail />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="billing/amount"
      element={
        <ModuleAccessGuard module="billing.activePlan">
          <BillingAmount />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="billing/ai"
      element={
        <ModuleAccessGuard module="billing.activePlan">
          <BillingAI />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="billing/active-plan"
      element={
        <ModuleAccessGuard module="billing.activePlan">
          <BillingActivePlan />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="billing/usage-history"
      element={
        <ModuleAccessGuard module="billing.activePlan">
          <BillingUsageHistory />
        </ModuleAccessGuard>
      }
    />
    {/* Prepaid wallet — also the Cashfree return_url for top-ups (?order_id=). */}
    <Route
      path="billing/wallet"
      element={
        <ModuleAccessGuard module="billing.activePlan">
          <BillingWallet />
        </ModuleAccessGuard>
      }
    />
    {/* No module guard: the `settings` key is hidden from the grant surface and has no
        backend requirePermission enforcement — sub-features are gated by their own keys
        (e.g. whatsapp for template routes below). */}
    <Route path="settings" element={<Settings />} />
    {/* WhatsApp Templates — its own page under Agents in the sidebar (was a
        Settings tab). Module-gated on 'whatsapp'; the plan feature
        (whatsappEnabled) locks the sidebar item and the page's own actions. */}
    <Route
      path="whatsapp-templates"
      element={
        <ModuleAccessGuard module="whatsapp">
          <WhatsAppTemplates />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="whatsapp-templates/new"
      element={
        <ModuleAccessGuard module="whatsapp">
          <WhatsAppTemplateNew />
        </ModuleAccessGuard>
      }
    />
    {/* Deep-linkable in-place EDIT of an existing template (opened in a new tab
        from the template pickers). Identity in the URL (?language=…) so the edit
        screen can load it without router state. This is the single path for fixing
        a template — including re-uploading media — so there's no separate
        fix-media screen to diverge from it. */}
    <Route
      path="whatsapp-templates/:name/edit"
      element={
        <ModuleAccessGuard module="whatsapp">
          <WhatsAppTemplateNew />
        </ModuleAccessGuard>
      }
    />
    <Route path="settings/whatsapp-templates/new" element={<LegacyTemplateNewRedirect />} />
    {/* Saved List / Button messages — a sibling of WhatsApp Templates under the
        same WhatsAppSectionTabs strip, gated on the same 'whatsapp' module. */}
    <Route
      path="whatsapp-interactive-messages"
      element={
        <ModuleAccessGuard module="whatsapp">
          <WhatsAppInteractiveMessages />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="whatsapp-interactive-messages/new"
      element={
        <ModuleAccessGuard module="whatsapp">
          <InteractiveMessageEditor />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="whatsapp-interactive-messages/:id"
      element={
        <ModuleAccessGuard module="whatsapp">
          <InteractiveMessageEditor />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="user-management"
      element={
        <ModuleAccessGuard module="userManagement">
          <UserManagement />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="campaigns"
      element={
        <ModuleAccessGuard module="campaigns">
          <CampaignList />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="campaigns/new"
      element={
        <ModuleAccessGuard module="campaigns">
          <CampaignForm />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="campaigns/:id/edit"
      element={
        <ModuleAccessGuard module="campaigns">
          <CampaignForm />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="campaigns/:id"
      element={
        <ModuleAccessGuard module="campaigns">
          <CampaignDetail />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="system-alerts"
      element={
        <ModuleAccessGuard module="systemAlerts">
          <SystemAlerts />
        </ModuleAccessGuard>
      }
    />
    <Route path="account" element={<MyAccount />} />
    <Route
      path="llm-config"
      element={
        <ModuleAccessGuard module="llm">
          <LLMConfig />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="automation/workflows"
      element={
        <ModuleAccessGuard module="automation">
          <Workflows />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="automation/api-connections"
      element={
        <ModuleAccessGuard module="automation">
          <ApiConnections />
        </ModuleAccessGuard>
      }
    />
    {/* Dedicated build/test/map page. `new` is a static segment, so it can't be read as an :id. */}
    <Route
      path="automation/api-connections/new"
      element={
        <ModuleAccessGuard module="automation">
          <ApiConnectionEditor />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="automation/api-connections/:id"
      element={
        <ModuleAccessGuard module="automation">
          <ApiConnectionEditor />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="automation/workflows/:id/executions"
      element={
        <ModuleAccessGuard module="automation">
          <WorkflowExecutions />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="automation/workflows/:id"
      element={
        <ModuleAccessGuard module="automation">
          <WorkflowCanvas />
        </ModuleAccessGuard>
      }
    />
    <Route
      path="web-chats"
      element={
        <ModuleAccessGuard module="leadForm">
          <LeadForm />
        </ModuleAccessGuard>
      }
    />
  </React.Fragment>
);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<AdminLogin />} />
          <Route path="/signup" element={<Signup />} />
          {/* PUBLIC — the emailed reset link lands on /reset-password?token=… */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/invite" element={<InviteAccept />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          {/* PUBLIC — reached mid Google-sign-in, before any session/tokens
              exist (see useAuth.ts#googlePreview), so this cannot sit behind
              ProtectedRoute. */}
          <Route path="/auth/google/confirm" element={<GoogleConfirm />} />

          {/* Identity-only — reachable by any authenticated user regardless of
              whether they have a tenant yet (that's the whole point of this
              page). requireTenant={false}: ProtectedRoute's normal "no tenant
              → redirect to /onboarding" rule would otherwise redirect this
              exact route to itself — an infinite loop. */}
          <Route element={<ProtectedRoute requireTenant={false} />}>
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/onboarding/pending" element={<OnboardingPending />} />
          </Route>

          <Route
            element={
              <ProtectedRoute />
            }
          >
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Navigate to="/admin/welcome" replace />} />
              {platformRoutes}
            </Route>
          </Route>

          {/* Root: public marketing homepage at "/", tenant app protected below it */}
          <Route path="/">
            <Route index element={<Home />} />
            <Route element={<ProtectedRoute />}>
              <Route element={<AdminLayout />}>{platformRoutes}</Route>
            </Route>
          </Route>

          {/* Public lead capture — no auth required */}
          <Route path="/form/:tenantSlug" element={<LeadCapture />} />

          <Route path="*" element={<div>404 Not Found</div>} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
